import * as ex from 'excalibur';
import { EntityType, PlantType, TileType } from '../types/enums';
import { PlantConfig } from '../types/interfaces';
import { TILE_SIZE } from '../config/constants';
import { Plant } from '../core/plant';
import { Grid } from '../core/grid';
import { SpriteManager } from '../managers/spriteManager';

export class PlantFactory {
  
  // Create a plant instance with type-specific configuration
  public static createPlant(
    plantType: PlantType,
    gridX: number,
    gridY: number,
    grid: Grid,
    growthStage?: number
  ): Plant {
    // Base configuration common to all plants
    const baseConfig: Partial<PlantConfig> = {
      type: EntityType.Plant,
      gridX,
      gridY,
      width: TILE_SIZE * 0.7,
      height: TILE_SIZE * 0.7,
      plantType,
      growthStage: growthStage !== undefined ? growthStage : 0
    };
    
    // Plant-specific configurations
    let config: PlantConfig;
    
    switch (plantType) {
      case PlantType.Tree:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#228B22'), // Forest green
          maxGrowthStage: 5,
          growthRate: 15000, // 15 seconds per growth stage
          interactable: true
        } as PlantConfig;
        break;
        
      case PlantType.Flower:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#FF69B4'), // Hot pink
          maxGrowthStage: 3,
          growthRate: 8000, // 8 seconds per growth stage
          interactable: true
        } as PlantConfig;
        break;
        
      case PlantType.Bush:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#32CD32'), // Lime green
          maxGrowthStage: 4,
          growthRate: 10000, // 10 seconds per growth stage
          interactable: true
        } as PlantConfig;
        break;
        
      case PlantType.Grass:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#90EE90'), // Light green
          maxGrowthStage: 2,
          growthRate: 5000, // 5 seconds per growth stage
          interactable: true
        } as PlantConfig;
        break;
        
      default:
        // Default configuration for unknown plant types
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#00FF00'), // Green
          maxGrowthStage: 3,
          growthRate: 10000,
          interactable: false
        } as PlantConfig;
    }
    
    // Create the plant instance
    const plant = new Plant({
      ...config,
      grid
    });
    
    // Apply sprite if available
    SpriteManager.applySprite(plant);
    
    return plant;
  }
  
  // Create plants in appropriate terrain types
  public static populateWorld(grid: Grid): Plant[] {
    const plants: Plant[] = [];
    
    // Iterate through the entire grid
    for (let y = 0; y < grid['tiles'].length; y++) {
      for (let x = 0; x < grid['tiles'][y].length; x++) {
        const tileType = grid.getTileAt(x, y);
        
        // Skip if there's already an entity here
        if (grid.getEntityAt(x, y) !== null) {
          continue;
        }
        
        let plant: Plant | null = null;
        
        // Add plants based on tile type with some randomness
        if (tileType === TileType.Grass && Math.random() < 0.03) {
          // On grass, add flowers or plain grass (reduced density)
          const plantType = Math.random() < 0.3 ? PlantType.Flower : PlantType.Grass;
          plant = this.createPlant(plantType, x, y, grid);
        } 
        else if (tileType === TileType.Forest && Math.random() < 0.2) {
          // In forests, add trees or bushes (reduced density)
          const plantType = Math.random() < 0.7 ? PlantType.Tree : PlantType.Bush;
          plant = this.createPlant(plantType, x, y, grid);
          
          // Trees in forests start more grown
          if (plantType === PlantType.Tree) {
            plant.growthStage = 2 + Math.floor(Math.random() * 3);
          }
        }
        
        // Add the plant if we created one
        if (plant) {
          grid.addEntity(plant, x, y);
          plants.push(plant);
        }
      }
    }
    
    return plants;
  }
  
  // Create a specific cluster of plants (for custom areas)
  public static createPlantCluster(
    grid: Grid,
    centerX: number,
    centerY: number,
    radius: number,
    plantType: PlantType,
    density: number = 0.6
  ): Plant[] {
    const plants: Plant[] = [];
    
    // Create plants in a circular area
    for (let y = centerY - radius; y <= centerY + radius; y++) {
      for (let x = centerX - radius; x <= centerX + radius; x++) {
        // Skip if outside grid bounds
        if (x < 0 || y < 0 || x >= grid['tiles'][0].length || y >= grid['tiles'].length) {
          continue;
        }
        
        // Skip if outside radius
        const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
        if (distance > radius) {
          continue;
        }
        
        // Skip if there's already an entity here
        if (grid.getEntityAt(x, y) !== null) {
          continue;
        }
        
        // Skip with probability based on density
        if (Math.random() > density) {
          continue;
        }
        
        // Plant can only be placed on appropriate tile
        const tileType = grid.getTileAt(x, y);
        
        if ((plantType === PlantType.Tree || plantType === PlantType.Bush) && 
            tileType !== TileType.Forest && tileType !== TileType.Grass) {
          continue;
        }
        
        if ((plantType === PlantType.Flower || plantType === PlantType.Grass) && 
            tileType !== TileType.Grass) {
          continue;
        }
        
        // Create the plant with random initial growth stage
        const growthStage = Math.floor(Math.random() * 3);
        const plant = this.createPlant(plantType, x, y, grid, growthStage);
        
        // Add to grid and result list
        grid.addEntity(plant, x, y);
        plants.push(plant);
      }
    }
    
    return plants;
  }
}